import React, { useEffect, useMemo, useState } from 'react';
import analyticsService, { WarmupHealthHistoryResponse, WarmupCurrentHealthResponse } from '../../../services/analyticsService';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';

interface Props {
  mailboxId: string;
}

const WarmupHealthPanel: React.FC<Props> = ({ mailboxId }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [premiumBlocked, setPremiumBlocked] = useState(false);
  const [history, setHistory] = useState<WarmupHealthHistoryResponse | null>(null);
  const [current, setCurrent] = useState<WarmupCurrentHealthResponse | null>(null);
  const [recomputeLoading, setRecomputeLoading] = useState(false);

  const load = async () => {
    if (!mailboxId) return;
    try {
      setPremiumBlocked(false);
      setError(null);
      setLoading(true);
      const [hist, curr] = await Promise.all([
        analyticsService.getWarmupHealthHistory(mailboxId),
        analyticsService.getCurrentWarmupHealth(mailboxId)
      ]);
      setHistory(hist);
      setCurrent(curr);
    } catch (e: any) {
      console.error('Failed to load warmup health', e);
      const msg = (e?.message || '').toLowerCase();
      if (msg.includes('premium') || msg.includes('forbidden') || msg.includes('403')) {
        setPremiumBlocked(true);
      } else {
        setError(e?.message || 'Failed to load warmup health');
      }
      setHistory(null);
      setCurrent(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mailboxId]);

  const data = useMemo(() => {
    if (!history?.series) return [];
    return history.series.map(p => ({
      date: new Date(p.date).toISOString().slice(0, 10),
      score: p.score,
      decision: p.decision,
    }));
  }, [history]);

  const decisionColor = (decision?: string) => {
    switch (decision) {
      case 'increase':
        return 'text-green-700 bg-green-100 border-green-200';
      case 'reduce':
        return 'text-yellow-700 bg-yellow-100 border-yellow-200';
      case 'pause':
        return 'text-red-700 bg-red-100 border-red-200';
      default:
        return 'text-gray-700 bg-gray-100 border-gray-200';
    }
  };

  const handleRecompute = async () => {
    if (!mailboxId) return;
    try {
      setRecomputeLoading(true);
      await analyticsService.recomputeWarmupHealth(mailboxId);
      await load();
    } catch (e: any) {
      console.error('Failed to recompute warmup health', e);
      const msg = (e?.message || '').toLowerCase();
      if (msg.includes('premium') || msg.includes('forbidden') || msg.includes('403')) {
        setPremiumBlocked(true);
      } else {
        setError(e?.message || 'Failed to recompute warmup health');
      }
    } finally {
      setRecomputeLoading(false);
    }
  };

  return (
    <div className="bg-white p-4 rounded-lg shadow border border-gray-200">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-lg font-semibold text-gray-800">Adaptive Warmup Health</h3>
        <div className="flex items-center gap-2">
          {current && (
            <span className={`px-2 py-1 text-xs font-semibold rounded border ${decisionColor(current.decision)}`}>
              Decision: {current.decision}
            </span>
          )}
          <button
            onClick={handleRecompute}
            disabled={!mailboxId || premiumBlocked || recomputeLoading}
            className={`px-3 py-1 text-sm rounded ${premiumBlocked ? 'bg-gray-200 text-gray-500' : 'bg-blue-600 text-white hover:bg-blue-700'} disabled:opacity-60`}
            aria-label="Recompute warmup health"
          >
            {recomputeLoading ? 'Recomputing...' : 'Recompute now'}
          </button>
        </div>
      </div>
      <p className="text-xs text-gray-500 mb-3">
        We automatically send up to 20 warmup emails per day from this mailbox and keep those messages out of your main inbox.
        Campaign sending is capped at 30 emails per day per mailbox to protect your domain reputation.
      </p>

      {premiumBlocked && (
        <div className="p-3 mb-3 bg-yellow-50 border border-yellow-200 rounded text-yellow-800">
          Premium required to view warmup health.
        </div>
      )}

      {error && (
        <div className="p-3 mb-3 bg-red-50 border border-red-200 rounded text-red-800">
          {error}
        </div>
      )}

      {loading ? (
        <div className="h-48 flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2">
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={data} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis domain={[0, 100]} />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="score" name="Health Score" stroke="#2563eb" dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
          <div className="space-y-2">
            <div className="p-3 rounded border border-gray-200">
              <div className="text-sm text-gray-500">Current Score</div>
              <div className="text-3xl font-bold text-gray-900">{current?.score ?? '—'}</div>
            </div>
            <div className="p-3 rounded border border-gray-200">
              <div className="text-sm text-gray-500">Latest Decision</div>
              <div className={`inline-block mt-1 px-2 py-1 text-xs font-semibold rounded border ${decisionColor(current?.decision)}`}>
                {current?.decision ?? '—'}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WarmupHealthPanel;
